import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import LegalClosingSection from '../components/LegalClosingSection';
import './Terms.css';

const trustCards = [
  {
    id: 'agreements',
    text: 'Project scope, price, and timelines are governed by written agreements, not website copy alone.',
    Icon: IconDocument,
  },
  {
    id: 'fair-use',
    text: 'Use this website and our materials only as these terms and applicable law allow.',
    Icon: IconEye,
  },
  {
    id: 'law',
    text: 'These terms are governed by the laws of India, with courts at Noida, Uttar Pradesh.',
    Icon: IconGlobe,
  },
  {
    id: 'updates',
    text: 'When we change these terms, we update this page and the Last updated date above.',
    Icon: IconCheck,
  },
];

const accordionSections = [
  {
    id: 'about',
    title: 'About Landmarks Construction',
    iconTone: 'about',
    Icon: IconBuilding,
    body: (
      <>
        <p>
          <span className="terms-brand-name">Landmarks Construction</span> is a construction and project delivery firm
          based in Noida, Uttar Pradesh, India. We undertake residential, commercial, and related civil and structural
          work, supporting clients from early planning through execution and handover.
        </p>
        <p>
          This website introduces our capabilities in general terms. All project-specific scope, price, timelines, and
          commitments are set out in written proposals, quotations, and contracts—not inferred from marketing or site
          copy alone.
        </p>
      </>
    ),
  },
  {
    id: 'interpretation',
    title: 'Interpretation',
    iconTone: 'interpret',
    Icon: IconDocument,
    body: (
      <>
        <p>
          In these Terms &amp; Conditions: <strong>&quot;Website&quot;</strong> means the online properties operated by{' '}
          <span className="terms-brand-name">Landmarks Construction</span>. <strong>&quot;Services&quot;</strong> means
          construction, project management, design coordination, consultancy, and related services we offer.{' '}
          <strong>&quot;Client&quot;</strong> or <strong>&quot;you&quot;</strong> means any person or entity using the
          Website or engaging us. <strong>&quot;we&quot;</strong>, <strong>&quot;us&quot;</strong>, and{' '}
          <strong>&quot;our&quot;</strong> refer to Landmarks Construction.
        </p>
        <p>
          Headings are for convenience only. Words importing the singular include the plural and vice versa. References
          to statutes include amendments and successor legislation. &quot;Including&quot; means &quot;including without
          limitation&quot;.
        </p>
      </>
    ),
  },
  {
    id: 'services',
    title: 'Services',
    iconTone: 'services',
    Icon: IconCheck,
    body: (
      <>
        <p>
          We provide civil, structural, and general contracting services for residential, commercial, and related
          projects, including planning support, execution, coordination with consultants, and handover assistance, as
          described on the Website or in separate proposals.
        </p>
        <p>
          The Website describes our capabilities in general terms. Specific scope, specifications, exclusions, warranties
          (if any), and deliverables are defined only in a written quotation, work order, or contract signed by both
          parties. Nothing on the Website obligates us to accept any project or to perform work until we confirm in
          writing.
        </p>
        <p>
          We may use subcontractors, specialists, or suppliers where appropriate, remaining responsible for delivery of our
          contractual obligations unless otherwise agreed in writing.
        </p>
        <p>
          Estimates, rate indications, or budget ranges provided online, by phone, or in preliminary meetings are
          non-binding unless expressly stated otherwise in writing. Final pricing depends on approved drawings,
          specifications, site conditions, statutory requirements, and market conditions at the time of agreement.
        </p>
        <p>
          A contract is formed when you and we execute a written agreement (including scope, price or rate basis, payment
          schedule, programme, and terms). Until then, no obligation exists to commence work. Oral understandings are not
          binding unless confirmed in writing.
        </p>
        <p>
          <strong>Variations:</strong> Any change to scope, specifications, or sequence must be documented (change order
          or written instruction). We may revise price and programme for authorised variations. Unauthorised changes
          directed on site may be refused or charged additionally once agreed in writing.
        </p>
        <p>
          Taxes (including GST), statutory levies, and third-party fees (approvals, utilities, testing) are as stated in
          the contract or borne as agreed. Unless included in our quote, such costs are your responsibility.
        </p>
        <p>
          Payment terms, milestones, retention, and security (if any) are set out in the contract. Unless otherwise
          agreed, invoices are due within the period stated on the invoice. Late payment may attract interest and may
          entitle us to suspend work after notice, where permitted by contract and law.
        </p>
        <p>
          You may not withhold payments unreasonably. Disputed amounts should be notified in writing with reasons;
          undisputed portions remain payable. We may apply payments against oldest outstanding invoices first unless
          specified otherwise.
        </p>
        <p>
          Cancellation or termination before completion is handled as per the contract (including payment for work done,
          materials ordered, and reasonable demobilisation). Deposits or advances may be non-refundable to the extent
          stated in writing.
        </p>
        <p>
          We will execute the work in accordance with the contract, approved drawings, and applicable building practices.
          Programme dates are estimates unless fixed as milestones in writing. Delays caused by force majeure, late
          approvals, late payments, variations, site conditions, or other factors outside our reasonable control may
          extend timelines without liability to the extent permitted by law.
        </p>
        <p>
          Neither party is liable for failure or delay due to events beyond reasonable control, including natural
          disasters, war, terrorism, riots, fire, epidemic, government orders, strikes, supply chain failures, or utility
          outages, provided the affected party uses reasonable efforts to mitigate and notify the other party.
        </p>
        <p>
          You or your representative may inspect work at reasonable times. Critical stages may require joint inspection
          before cover-up where specified. Snagging lists should be raised promptly; we will endeavour to rectify agreed
          defects within a reasonable period subject to the contract.
        </p>
        <p>
          Handover occurs as defined in the contract (e.g. upon completion certificate, final payment milestone, or
          written acceptance). Risk and care of the works may pass to you on handover as agreed. Warranties or maintenance
          obligations, if any, are only those expressly stated in the contract.
        </p>
      </>
    ),
  },
  {
    id: 'eligibility',
    title: 'Eligibility',
    iconTone: 'eligibility',
    Icon: IconUser,
    body: (
      <>
        <p>
          By accessing the Website, submitting an enquiry, requesting a callback, or using our Services, you agree to
          these Terms &amp; Conditions and our <Link to="/privacy">Privacy Policy</Link>. If you do not agree, do not use
          the Website or Services.
        </p>
        <p>
          You represent that you are at least 18 years of age and have legal capacity to contract. We may refuse service,
          terminate access, or reject enquiries at our discretion where permitted by law.
        </p>
      </>
    ),
  },
  {
    id: 'user-responsibilities',
    title: 'User Responsibilities',
    iconTone: 'user',
    Icon: IconUserCog,
    body: (
      <>
        <p>You agree to:</p>
        <ul>
          <li>Provide accurate information about the site, ownership, encumbrances, and intended use.</li>
          <li>
            Obtain and maintain necessary rights to the land, statutory approvals, sanctions, NOCs, and utility
            connections unless our contract explicitly assigns these to us.
          </li>
          <li>
            Grant safe and reasonable access to the site, including storage and lay-down areas where agreed, and address
            neighbourhood or society permissions if required.
          </li>
          <li>
            Appoint and coordinate architects, structural engineers, MEP consultants, and other specialists as agreed,
            and supply approved drawings and specifications in a timely manner.
          </li>
          <li>
            Make decisions and approvals within agreed timeframes so that work is not delayed; delays attributable to you
            may extend the programme and affect cost.
          </li>
          <li>
            Comply with applicable laws and not require us to perform work that is unsafe or non-compliant with codes or
            regulations.
          </li>
        </ul>
        <p>
          All content on the Website (text, graphics, logos, photographs, videos, layouts, and software) is owned by us or
          our licensors and protected by copyright, trademark, and other laws. You receive a limited, non-exclusive,
          non-transferable licence to view the Website for personal or internal business use.
        </p>
        <div className="terms-note-grey">
          <p>
            <span className="terms-note-label">Important: </span>
            These rules apply to the website and materials of{' '}
            <span className="terms-brand-name">Landmarks Construction</span> only. We do not control embedded third-party
            widgets, advertisers, or external sites you reach through links—their terms and practices are theirs alone.
          </p>
        </div>
        <p>
          You must not copy, modify, distribute, scrape, mirror, frame, or exploit the Website or its content without
          prior written consent, except as allowed by law. You must not:
        </p>
        <ul>
          <li>Use the Website for unlawful, fraudulent, or harmful purposes.</li>
          <li>Introduce viruses, malware, or attempt unauthorised access to our systems.</li>
          <li>Overload, disrupt, or probe the Website or related infrastructure.</li>
          <li>Harvest data or contact information through automated means without consent.</li>
          <li>Impersonate any person or misrepresent your affiliation.</li>
        </ul>
        <p>
          Feedback or suggestions you provide may be used by us without obligation to compensate you, unless we agree
          otherwise in writing.
        </p>
      </>
    ),
  },
  {
    id: 'liability',
    title: 'Limitation of Liability',
    iconTone: 'liability',
    Icon: IconShield,
    body: (
      <>
        <p>
          Setting clear expectations matters. While the Website is provided for information, we take reasonable steps to
          present our services accurately and to perform contracted work professionally.
        </p>
        <div className="terms-safeguards-box">
          <ul className="terms-safeguards-list">
            <li>
              <span className="terms-safeguard-check" aria-hidden="true">
                <svg viewBox="0 0 12 10" width="11" height="9" fill="none">
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Contractual Basis:</strong> Binding scope, price, and deliverables are defined in your signed
                agreement—not inferred from marketing or website copy alone.
              </span>
            </li>
            <li>
              <span className="terms-safeguard-check" aria-hidden="true">
                <svg viewBox="0 0 12 10" width="11" height="9" fill="none">
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Professional Standards:</strong> We aim to execute Services in line with agreed drawings,
                specifications, and applicable building practices.
              </span>
            </li>
            <li>
              <span className="terms-safeguard-check" aria-hidden="true">
                <svg viewBox="0 0 12 10" width="11" height="9" fill="none">
                  <path
                    d="M1 5l3.5 3.5L11 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <span>
                <strong>Compliance Focus:</strong> We work within statutory and code requirements as applicable to the
                work agreed in your contract.
              </span>
            </li>
          </ul>
        </div>
        <div className="terms-important-warning">
          <p>
            <strong>Important Note:</strong> The Website and its content are still provided on an &quot;as is&quot; and
            &quot;as available&quot; basis. To the maximum extent permitted by law, we disclaim implied warranties such as
            merchantability, fitness for a particular purpose, or non-infringement for the site itself. We do not warrant
            uninterrupted or error-free operation of the Website. Use of the site involves inherent risks you accept.
          </p>
        </div>
        <p>
          Illustrations, renders, and sample specifications on the Website are indicative only. Actual finishes, dimensions,
          timelines, and performance depend on your executed contract and site conditions. For project warranties or
          remedies, refer to your written agreement with us.
        </p>
        <p>
          To the fullest extent permitted by applicable law, we are not liable for any indirect, incidental, special,
          consequential, exemplary, or punitive damages, or for loss of profits, revenue, data, goodwill, or business
          opportunities, arising from or related to the Website or Services, whether in contract, tort, or otherwise.
        </p>
        <p>
          Our aggregate liability arising from or related to the Website (excluding liability under a signed construction
          contract, which is governed by that contract) shall not exceed the greater of (a) the fees you paid us in the
          twelve months preceding the claim, or (b) INR 10,000.
        </p>
        <p>
          You agree to indemnify, defend, and hold harmless{' '}
          <span className="terms-brand-name">Landmarks Construction</span>, its directors, officers, employees, and agents
          from and against any claims, damages, losses, costs, and expenses (including reasonable legal fees) arising
          from: (a) your breach of these terms; (b) your misuse of the Website; (c) your site, approvals, or instructions
          that violate law or third-party rights; or (d) negligence or wilful misconduct attributable to you or your
          representatives.
        </p>
        <p>
          Nothing in these terms excludes or limits liability that cannot be excluded or limited under applicable law,
          including liability for death or personal injury caused by negligence where such limitation is prohibited.
        </p>
      </>
    ),
  },
  {
    id: 'third-party',
    title: 'Third-Party Links',
    iconTone: 'links',
    Icon: IconLink,
    body: (
      <>
        <p>
          The Website may contain links to third-party websites or integrations. We do not control and are not
          responsible for third-party content, policies, or practices. Accessing third-party sites is at your risk.
        </p>
      </>
    ),
  },
  {
    id: 'account',
    title: 'Account Registration',
    iconTone: 'account',
    Icon: IconPadlock,
    body: (
      <>
        <p>
          Where you submit information through enquiry forms, callback requests, or other contact channels, you agree to
          provide accurate details and to keep any credentials or one-time codes confidential if we introduce registered
          access in the future.
        </p>
        <p>
          You are responsible for activity carried out using your contact details or account identifiers unless you notify
          us promptly of unauthorised use. We may suspend or terminate access where we reasonably suspect abuse or a
          security risk.
        </p>
      </>
    ),
  },
  {
    id: 'privacy-data',
    title: 'Privacy and Data Protection',
    iconTone: 'privacy-data',
    Icon: IconShield,
    body: (
      <>
        <p>
          Personal data is processed as described in our <Link to="/privacy">Privacy Policy</Link>. By submitting forms or
          contacting us, you consent to us using your information to respond, provide Services, and comply with law, as
          outlined there.
        </p>
        <p>
          We may send service-related messages (calls, SMS, email, WhatsApp where you have provided numbers) regarding your
          enquiry or project. Marketing communications will be sent in line with applicable law and your preferences where
          opt-out is required.
        </p>
      </>
    ),
  },
  {
    id: 'amendments',
    title: 'Amendments',
    iconTone: 'amend',
    Icon: IconRefresh,
    body: (
      <>
        <p>
          We may amend these terms at any time. The &quot;Last updated&quot; date will be revised. Material changes may be
          highlighted on the Website where practicable. Continued use after posting constitutes acceptance of the updated
          terms where allowed by applicable law. If you do not accept changes, you must stop using the Website and
          Services.
        </p>
        <p>
          These terms are governed by the laws of India. Subject to mandatory provisions, courts at Noida, Uttar Pradesh,
          shall have exclusive jurisdiction over disputes arising from the Website or these terms, except where a separate
          construction contract specifies a different mechanism (which will prevail for that project).
        </p>
        <p>
          Disputes should first be raised in writing with our management. Parties will attempt good-faith resolution before
          initiating legal proceedings, unless urgent injunctive relief is required.
        </p>
        <p>
          If any provision is held invalid, the remainder remains in effect. Failure to enforce a provision is not a
          waiver. You may not assign your rights without our consent; we may assign our rights in connection with a
          merger, sale, or reorganisation. These terms together with the Privacy Policy constitute the entire agreement
          regarding the Website to the exclusion of prior oral representations, except as superseded by a signed project
          contract.
        </p>
        <p>
          <strong>Contact:</strong>{' '}
          <a href="mailto:info@landmarksconstruction.in">info@landmarksconstruction.in</a>,{' '}
          <a href="tel:+919319219252">+91-93192-19252</a>. Registered office: G-35, Floor 1, Sector 3, Noida – 201303,
          Uttar Pradesh, India.
        </p>
        <p>
          <Link to="/">Home</Link> · <Link to="/contact">Contact</Link> · <Link to="/privacy">Privacy Policy</Link>
        </p>
      </>
    ),
  },
];

function IconBuilding({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M4 21h16M4 21V10l6-3v14M10 7l10 5v9M10 21V12h2.5v9M15 15.5V18"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLink({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M10 13a5 5 0 01-7-7l1-1a5 5 0 017 7M14 11a5 5 0 017 7l-1 1a5 5 0 01-7-7M8.5 15.5l7-7"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconUserCog({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <circle cx="9" cy="7" r="3" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M4 20v-1a5 5 0 015-5h1M17.5 14.5l.35 1.2 1.2.35-1.2.35-.35 1.2-1.2-.35-1.2.35-.35-1.2-1.2-.35 1.2-.35.35-1.2 1.2.35 1.2-.35z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconRefresh({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M4 4v6h6M20 20v-6h-6M5.64 5.64A9 9 0 0118.36 18.36M18.36 5.64A9 9 0 015.64 18.36"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconDocument({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M14 2H7a2 2 0 00-2 2v16a2 2 0 002 2h10a2 2 0 002-2V7l-5-5z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <path d="M14 2v5h5M9 13h6M9 17h4" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
    </svg>
  );
}

function IconEye({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <path
        d="M2 12s4-6 10-6 10 6 10 6-4 6-10 6-10-6-10-6z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}

function IconGlobe({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M3 12h18M12 3a14 14 0 000 18M12 3a14 14 0 010 18"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconCheck({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 12l2.5 2.5L16 9" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconUser({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
      <path
        d="M6 20v-1c0-2.5 2-4.5 6-4.5s6 2 6 4.5v1"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
      />
    </svg>
  );
}

function IconPadlock({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <rect x="6" y="10" width="12" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" />
      <path d="M9 10V7a3 3 0 016 0v3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconChart({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path d="M4 20V10M10 20V4M16 20v-6M22 20V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  );
}

function IconShield({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden>
      <path
        d="M12 3L5 6v5c0 4.5 2.8 8.5 7 10 4.2-1.5 7-5.5 7-10V6l-7-3z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function Terms() {
  const baseId = useId();
  const [openKey, setOpenKey] = useState(null);

  const toggle = (key) => {
    setOpenKey((prev) => (prev === key ? null : key));
  };

  return (
    <div className="terms-page">
      <header className="terms-hero">
        <div className="terms-hero-inner">
          <div className="terms-hero-badge" aria-hidden="true">
            <IconDocument className="terms-hero-badge-icon" />
          </div>
          <h1 className="terms-hero-title">Terms &amp; Conditions</h1>
          <p className="terms-hero-tagline">
            Complete terms for using our website and for construction and related services we provide.
          </p>
          <p className="terms-hero-date">Last Updated: January 11, 2026</p>
        </div>
      </header>

      <div className="terms-trust-strip">
        <div className="page-wrap terms-main-inner terms-trust-inner">
          <ul className="terms-trust-grid">
            {trustCards.map(({ id, text, Icon }) => (
              <li key={id} className="terms-trust-card">
                <span className="terms-trust-card-icon" aria-hidden>
                  <Icon className="terms-trust-svg" />
                </span>
                <p className="terms-trust-card-text">{text}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="terms-main">
        <div className="page-wrap terms-main-inner">
          <section className="terms-overview" aria-labelledby="terms-overview-heading">
            <h2 id="terms-overview-heading" className="terms-overview-title">
              <span className="terms-overview-icon-wrap" aria-hidden="true">
                <IconDocument />
              </span>
              An Overview
            </h2>
            <div className="terms-overview-prose">
              <p>
                These Terms &amp; Conditions apply to your use of this website and to enquiries and services provided by{' '}
                <span className="terms-brand-name">Landmarks Construction</span>. They work together with our{' '}
                <Link to="/privacy">Privacy Policy</Link>. A separate written contract for your project may add or
                override specific commercial terms; where there is a conflict for that project, the signed contract
                usually prevails.
              </p>
              <p>
                Please read all sections below. If you need clarification before starting work or sharing site
                information, contact us using the details in the Amendments section.
              </p>
            </div>
          </section>

          <div className="terms-agreement-callout" role="note">
            <div className="terms-agreement-callout-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="26" height="26" fill="none">
                <path
                  d="M8 12.5l2.5 2.5L16 9"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="terms-agreement-callout-body">
              <h3 className="terms-agreement-callout-title">Agreement to Terms</h3>
              <p className="terms-agreement-callout-text">
                By visiting our website or using our services, you acknowledge that you have read, understood, and agree
                to be bound by these Terms. These terms create clarity, protect all parties involved, and establish a
                transparent professional relationship from the first interaction onward.
              </p>
            </div>
          </div>

          <div className="terms-accordions" role="list">
            {accordionSections.map((section) => {
              const panelId = `${baseId}-${section.id}`;
              const isOpen = openKey === section.id;
              const { Icon } = section;
              return (
                <div key={section.id} className="terms-acc-item" role="listitem">
                  <button
                    type="button"
                    id={`${panelId}-btn`}
                    className="terms-acc-trigger"
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => toggle(section.id)}
                  >
                    <span
                      className={`terms-acc-trigger-icon terms-acc-trigger-icon--${section.iconTone}`}
                      aria-hidden="true"
                    >
                      <Icon />
                    </span>
                    <span className="terms-acc-trigger-title">{section.title}</span>
                    <span className={`terms-acc-chevron${isOpen ? ' is-open' : ''}`} aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="20" height="20" fill="none">
                        <path
                          d="M6 9l6 6 6-6"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </button>
                  <div
                    id={panelId}
                    role="region"
                    aria-labelledby={`${panelId}-btn`}
                    className="terms-acc-panel"
                    hidden={!isOpen}
                  >
                    <div className="terms-acc-panel-inner">{section.body}</div>
                  </div>
                </div>
              );
            })}
          </div>

          <LegalClosingSection variant="terms" />
        </div>
      </div>
    </div>
  );
}
